import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface Expense {
  id: string;
  space_id: string;
  category_id: string;
  logged_by: string;
  paid_by: string;
  amount_cents: number;
  description: string;
  action: 'split' | 'keep';
  split_ratio: number;
  is_fixed_autolog: boolean;
  is_archived: boolean;
  created_at: string;
  category?: { name: string; emoji: string };
  excluded_members?: string[];
}

interface Settlement {
  id: string;
  space_id: string;
  settled_by: string;
  from_member: string;
  to_member: string;
  amount_cents: number;
  note: string;
  created_at: string;
}

interface BalanceEntry {
  member_id: string;
  net_cents: number;
  owes_cents: number;
  owed_cents: number;
}

interface ExpenseState {
  expenses: Expense[];
  settlements: Settlement[];
  isLoading: boolean;
  prefill: { categoryId?: string; amountCents?: number } | null;
  fetchExpenses: (spaceId: string) => Promise<void>;
  createExpense: (expense: Partial<Expense>) => Promise<void>;
  updateExpense: (id: string, updates: Partial<Expense>) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  fetchSettlements: (spaceId: string) => Promise<void>;
  createSettlement: (settlement: Partial<Settlement>) => Promise<void>;
  subscribeToChanges: (spaceId: string) => () => void;
  computeBalances: (currentMemberId: string, memberIds: string[]) => BalanceEntry[];
  setPrefill: (prefill: { categoryId?: string; amountCents?: number } | null) => void;
}

const dedupeById = <T extends { id?: string }>(items: T[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item?.id) {
      return true;
    }

    if (seen.has(item.id)) {
      return false;
    }

    seen.add(item.id);
    return true;
  });
};

const normalizeCents = (value: unknown) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount) : 0;
};

const normalizeSplitRatio = (value: unknown) => {
  const ratio = Number(value);
  if (!Number.isFinite(ratio)) {
    return 50;
  }

  return Math.min(100, Math.max(0, Math.round(ratio)));
};

const getOtherShareCents = (expense: Expense) => (
  Math.round((normalizeCents(expense.amount_cents) * (100 - normalizeSplitRatio(expense.split_ratio))) / 100)
);

const allocateDebtShares = (expense: Expense, debtorIds: string[]): Record<string, number> => {
  const eligibleDebtors = Array.from(new Set(debtorIds.filter(Boolean))).sort();

  if (eligibleDebtors.length === 0) {
    return {};
  }

  const otherShare = getOtherShareCents(expense);
  const baseShare = Math.trunc(otherShare / eligibleDebtors.length);
  const remainder = otherShare % eligibleDebtors.length;

  return Object.fromEntries(
    eligibleDebtors.map((memberId, index) => [
      memberId,
      baseShare + (index < remainder ? 1 : 0),
    ])
  );
};

export const useExpenseStore = create<ExpenseState>((set, get) => ({
  expenses: [],
  settlements: [],
  isLoading: false,
  prefill: null,

  fetchExpenses: async (spaceId) => {
    set({ isLoading: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`/api/expenses/${spaceId}`, {
        headers: {
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });

      const payload = await response.json().catch(() => []);
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to fetch expenses');
      }

      set({ expenses: Array.isArray(payload) ? dedupeById(payload) : [] });
    } finally {
      set({ isLoading: false });
    }
  },

  createExpense: async (expense) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch('/api/expenses', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(expense),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to create expense');
    }

    set({
      expenses: dedupeById([payload, ...get().expenses]),
    });
    set({ prefill: null });
  },

  updateExpense: async (id, updates) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/expenses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(updates),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to update expense');
    }

    const existingExpense = get().expenses.find((expense) => expense.id === id);
    const nextExpense = {
      ...existingExpense,
      ...updates,
      ...payload,
      id: payload.id || id,
      category: payload.category ?? existingExpense?.category,
    } as Expense;

    set({
      expenses: dedupeById([
        nextExpense,
        ...get().expenses.filter((expense) => expense.id !== id),
      ]),
    });
  },

  deleteExpense: async (id) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/expenses/${id}`, {
      method: 'DELETE',
      headers: {
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to delete expense');
    }

    set({ expenses: get().expenses.filter(e => e.id !== id) });
  },

  fetchSettlements: async (spaceId) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/settlements/${spaceId}`, {
      headers: {
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
    });

    const payload = await response.json().catch(() => []);
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to fetch settlements');
    }

    set({ settlements: Array.isArray(payload) ? dedupeById(payload) : [] });
  },

  createSettlement: async (settlement) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch('/api/settlements', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(settlement),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to create settlement');
    }

    set({
      settlements: dedupeById([payload, ...get().settlements]),
    });
  },

  subscribeToChanges: (spaceId) => {
    const subscription = supabase
      .channel(`expenses:${spaceId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'expenses',
        filter: `space_id=eq.${spaceId}`
      }, () => {
        get().fetchExpenses(spaceId);
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'settlements',
        filter: `space_id=eq.${spaceId}`
      }, () => {
        get().fetchSettlements(spaceId);
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  },

  computeBalances: (currentMemberId, memberIds) => {
    const { expenses, settlements } = get();
    const netByMember: Record<string, number> = {};
    const otherMemberIds = memberIds.filter((memberId) => memberId !== currentMemberId);

    otherMemberIds.forEach((memberId) => {
      netByMember[memberId] = 0;
    });

    expenses
      .filter((expense) => expense.action === 'split' && !expense.is_archived)
      .forEach((expense) => {
        const excludedMembers = expense.excluded_members || [];
        const eligibleDebtors = memberIds.filter(
          (memberId) => memberId !== expense.paid_by && !excludedMembers.includes(memberId)
        );

        if (eligibleDebtors.length === 0) {
          return;
        }

        const debtShares = allocateDebtShares(expense, eligibleDebtors);

        if (expense.paid_by === currentMemberId) {
          Object.entries(debtShares).forEach(([memberId, shareCents]) => {
            if (memberId !== currentMemberId) {
              netByMember[memberId] = (netByMember[memberId] || 0) + shareCents;
            }
          });
          return;
        }

        if (eligibleDebtors.includes(currentMemberId)) {
          netByMember[expense.paid_by] =
            (netByMember[expense.paid_by] || 0) - (debtShares[currentMemberId] || 0);
        }
      });

    settlements.forEach(s => {
      if (s.from_member === currentMemberId) {
        if (!netByMember[s.to_member]) netByMember[s.to_member] = 0;
        netByMember[s.to_member] += s.amount_cents;
      } else if (s.to_member === currentMemberId) {
        if (!netByMember[s.from_member]) netByMember[s.from_member] = 0;
        netByMember[s.from_member] -= s.amount_cents;
      }
    });

    return Object.entries(netByMember).map(([memberId, net]) => ({
      member_id: memberId,
      net_cents: Math.round(net),
      owes_cents: Math.round(Math.max(0, -net)), // I owe them
      owed_cents: Math.round(Math.max(0, net)),   // They owe me
    }));
  },

  setPrefill: (prefill) => set({ prefill }),
}));
